import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import GuestStatusCard from "../components/GuestStatusCard";
import GuestToastStack from "../components/GuestToastStack";
import MenuCategoryList from "../components/MenuCategoryList";
import MobileCart from "../components/MobileCart";
import { useCart } from "../context/CartContext";
import { useData } from "../context/DataProvider";
import useGuestFeedback from "../hooks/useGuestFeedback";
import {
  clearCheckoutResumeState,
  getCheckoutResumeState,
  markCheckoutResumeProcessing,
  setCheckoutResumePending,
} from "../utils/checkoutResume";
import { buildGuestLoginRedirectOptions, isAuthRecoveryError } from "../utils/authRecovery";
import { getConfig } from "../config";
import {
  getApiErrorMessage,
  getQrSessionMessage,
  getRecoveryReason,
  isQrSessionError,
  requestAssistance,
  submitOrder,
} from "../services/apiService";

const STALE_MENU_ERROR_CODES = new Set(["ITEM_NOT_ORDERABLE", "ITEM_UNAVAILABLE"]);

const OrderPage = () => {
  const { guestAuthEnabled } = getConfig();
  const { getAccessTokenSilently, isAuthenticated, isLoading: authLoading, loginWithRedirect } = useAuth0();
  const { menu, fetchMenuData } = useData();
  const { cart, clearCart, orderNote, removeItemsByIds, syncCartContext } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [sessionError, setSessionError] = useState(null);
  const [sessionErrorCode, setSessionErrorCode] = useState(null);
  const [cartContextNoticeVisible, setCartContextNoticeVisible] = useState(false);
  const [assistancePhase, setAssistancePhase] = useState("idle");
  const [checkoutPhase, setCheckoutPhase] = useState("idle");
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const resumeAttemptedRef = useRef(false);
  const categoryBarRef = useRef(null);
  const categoryChipRefs = useRef(new Map());
  const abandonedRedirectHandledRef = useRef(false);
  const { feedbackItems, pushFeedback, dismissFeedback } = useGuestFeedback();

  const categories = useMemo(
    () =>
      menu
        ? menu.reduce((acc, item) => {
            const categoryId = item.category_id || "uncategorized";
            const categoryName = item.category_name || "Diğer";
            if (!acc.find((cat) => cat.id === categoryId)) {
              acc.push({ id: categoryId, name: categoryName });
            }
            return acc;
          }, [])
        : [],
    [menu]
  );

  useEffect(() => {
    if (categories.length === 0) {
      setActiveCategoryId(null);
      return;
    }

    if (!categories.find((category) => category.id === activeCategoryId)) {
      setActiveCategoryId(categories[0].id);
    }
  }, [activeCategoryId, categories]);

  const getScrollAnchorOffset = useCallback(() => {
    if (typeof window === "undefined") {
      return 20;
    }

    const navbar = document.querySelector(".guest-navbar");
    const navbarRect = navbar?.getBoundingClientRect();
    const navbarHeight = navbarRect && navbarRect.top <= 0 && navbarRect.bottom > 0 ? navbarRect.bottom : 0;

    const categoryBarRect = categoryBarRef.current?.getBoundingClientRect();
    const categoryBarHeight =
      categoryBarRect && categoryBarRect.top <= 0 && categoryBarRect.bottom > 0 ? categoryBarRect.height : 0;

    return navbarHeight + categoryBarHeight + 20;
  }, []);

  const scrollToCategory = useCallback((categoryId) => {
    const element = document.getElementById(`category-${categoryId}`);
    if (element) {
      const offsetTop = window.scrollY + element.getBoundingClientRect().top - getScrollAnchorOffset();
      window.scrollTo({
        top: offsetTop,
        behavior: "smooth",
      });
    }
  }, [getScrollAnchorOffset]);

  useEffect(() => {
    if (!activeCategoryId) {
      return;
    }

    categoryChipRefs.current.get(activeCategoryId)?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [activeCategoryId]);

  useEffect(() => {
    if (categories.length === 0) {
      return undefined;
    }

    let frameId = null;

    const updateActiveCategoryFromScroll = () => {
      frameId = null;

      const anchorOffset = getScrollAnchorOffset();
      let nextCategoryId = categories[0].id;

      categories.forEach((category) => {
        const section = document.getElementById(`category-${category.id}`);
        if (!section) {
          return;
        }

        if (section.getBoundingClientRect().top <= anchorOffset) {
          nextCategoryId = category.id;
        }
      });

      setActiveCategoryId((current) => (current === nextCategoryId ? current : nextCategoryId));
    };

    const scheduleUpdate = () => {
      if (frameId !== null) {
        return;
      }

      frameId = window.requestAnimationFrame(updateActiveCategoryFromScroll);
    };

    scheduleUpdate();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
    };
  }, [categories, getScrollAnchorOffset]);

  const submitCurrentCart = useCallback(
    async (token) => {
      const orderItems = cart.map((item) => ({
        itemId: item.id,
        quantity: item.quantity,
        notes: item.notes || undefined,
        ...(item.selectedModifierOptions?.length
          ? { selectedModifierOptions: item.selectedModifierOptions }
          : {}),
      }));

      try {
        await submitOrder(token, orderItems, orderNote || undefined);
        clearCheckoutResumeState();
        clearCart();
        navigate("/order-summary", {
          state: {
            checkoutFeedback: {
              variant: "success",
              title: "Siparişiniz alındı",
              message: "Siparişiniz mutfağa iletildi. Durumunu aşağıda takip edebilirsiniz.",
            },
          },
        });
      } catch (error) {
        clearCheckoutResumeState();
        throw error;
      }
    },
    [cart, clearCart, navigate, orderNote]
  );

  const handleStaleCartRecovery = useCallback(async (error) => {
    const blockedItemIds = Array.isArray(error?.payload?.itemIds) ? error.payload.itemIds : [];
    await fetchMenuData();
    removeItemsByIds(blockedItemIds);
    pushFeedback({
      variant: "warning",
      title: "Bazı ürünler artık siparişe açık değil",
      message: "Sepetiniz güncellendi. Lütfen siparişinizi tekrar kontrol edin.",
    });
  }, [fetchMenuData, pushFeedback, removeItemsByIds]);

  const beginCheckoutLogin = useCallback(async () => {
    abandonedRedirectHandledRef.current = false;
    resumeAttemptedRef.current = false;
    setCheckoutResumePending("/order");
    setCheckoutPhase("redirecting");

    try {
      await loginWithRedirect(buildGuestLoginRedirectOptions("/order"));
    } catch (error) {
      clearCheckoutResumeState();
      setCheckoutPhase("idle");
      pushFeedback({
        variant: "danger",
        title: "Giriş başlatılamadı",
        message: getApiErrorMessage(error, "Giriş sayfasına yönlendirme sırasında hata oluştu."),
      });
    }
  }, [loginWithRedirect, pushFeedback]);

  useEffect(() => {
    let isActive = true;

    const initializeApp = async () => {
      setLoading(true);
      setHasValidSession(false);
      setSessionError(null);
      setSessionErrorCode(null);
      setCartContextNoticeVisible(false);
      setAssistancePhase("idle");

      try {
        const menuResult = await fetchMenuData();
        const cartContextResult = syncCartContext(menuResult.qrContext);
        if (!isActive) {
          return;
        }
        if (cartContextResult.didClear) {
          setCartContextNoticeVisible(true);
        }
        setHasValidSession(true);
      } catch (error) {
        if (!isActive) {
          return;
        }

        console.error("Error validating session:", error);
        setHasValidSession(false);
        setSessionErrorCode(error?.code || null);
        setSessionError(
          isQrSessionError(error)
            ? getQrSessionMessage(error.code)
            : "Masa oturumu doğrulanamadı. Lütfen QR kodunu tekrar tarayın."
        );
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    initializeApp();

    return () => {
      isActive = false;
    };
  }, [fetchMenuData, syncCartContext]);

  useEffect(() => {
    if (cart.length === 0) {
      clearCheckoutResumeState();
      resumeAttemptedRef.current = false;
    }
  }, [cart.length]);

  useEffect(() => {
    if (checkoutPhase !== "redirecting") {
      abandonedRedirectHandledRef.current = false;
    }
  }, [checkoutPhase]);

  useEffect(() => {
    if (!guestAuthEnabled) return;
    if (!hasValidSession || authLoading || !isAuthenticated || cart.length === 0 || resumeAttemptedRef.current) {
      return;
    }

    const resumeState = getCheckoutResumeState();
    if (!resumeState || resumeState.status !== "pending") {
      return;
    }

    resumeAttemptedRef.current = true;
    markCheckoutResumeProcessing();
    setCheckoutPhase("submitting");

    const resumeCheckout = async () => {
      try {
        const token = await getAccessTokenSilently();
        await submitCurrentCart(token);
      } catch (error) {
        if (isAuthRecoveryError(error)) {
          await beginCheckoutLogin();
          return;
        }

        setCheckoutPhase("idle");
        if (isQrSessionError(error)) {
          setHasValidSession(false);
          setSessionErrorCode(error.code);
          setSessionError(getQrSessionMessage(error.code));
          return;
        }

        if (STALE_MENU_ERROR_CODES.has(error?.code)) {
          await handleStaleCartRecovery(error);
          return;
        }

        pushFeedback({
          variant: "danger",
          title: "Sipariş gönderilemedi",
          message: getApiErrorMessage(error, "Sipariş gönderilirken hata oluştu."),
        });
      }
    };

    resumeCheckout();
  }, [
    guestAuthEnabled,
    authLoading,
    beginCheckoutLogin,
    cart.length,
    getAccessTokenSilently,
    hasValidSession,
    isAuthenticated,
    pushFeedback,
    submitCurrentCart,
    handleStaleCartRecovery,
  ]);

  useEffect(() => {
    const handlePossibleAbandonedRedirect = () => {
      if (!guestAuthEnabled || checkoutPhase !== "redirecting" || authLoading || isAuthenticated || abandonedRedirectHandledRef.current) {
        return;
      }

      const resumeState = getCheckoutResumeState();
      if (resumeState?.status === "processing") {
        return;
      }

      abandonedRedirectHandledRef.current = true;
      clearCheckoutResumeState();
      resumeAttemptedRef.current = false;
      setCheckoutPhase("idle");
      pushFeedback({
        variant: "info",
        title: "Giriş tamamlanmadı",
        message: "Sipariş vermek için tekrar deneyebilirsiniz.",
      });
    };

    window.addEventListener("pageshow", handlePossibleAbandonedRedirect);
    window.addEventListener("focus", handlePossibleAbandonedRedirect);

    return () => {
      window.removeEventListener("pageshow", handlePossibleAbandonedRedirect);
      window.removeEventListener("focus", handlePossibleAbandonedRedirect);
    };
  }, [authLoading, checkoutPhase, isAuthenticated, pushFeedback]);

  const handleSubmitOrder = async () => {
    if (cart.length === 0 || checkoutPhase !== "idle") {
      return;
    }

    if (guestAuthEnabled && !isAuthenticated) {
      await beginCheckoutLogin();
      return;
    }

    setCheckoutPhase("submitting");

    try {
      const token = guestAuthEnabled ? await getAccessTokenSilently() : null;
      await submitCurrentCart(token);
    } catch (error) {
      if (guestAuthEnabled && isAuthRecoveryError(error)) {
        await beginCheckoutLogin();
        return;
      }

      setCheckoutPhase("idle");
      if (isQrSessionError(error)) {
        setHasValidSession(false);
        setSessionErrorCode(error.code);
        setSessionError(getQrSessionMessage(error.code));
        return;
      }

      if (STALE_MENU_ERROR_CODES.has(error?.code)) {
        await handleStaleCartRecovery(error);
        return;
      }

      pushFeedback({
        variant: "danger",
        title: "Sipariş gönderilemedi",
        message: getApiErrorMessage(error, "Sipariş gönderilirken hata oluştu."),
      });
    }
  };

  const handleRequestAssistance = async () => {
    if (assistancePhase !== "idle") {
      return;
    }

    setAssistancePhase("requesting");

    try {
      await requestAssistance();
      setAssistancePhase("requested");
      pushFeedback({
        variant: "success",
        title: "Garson haberdar edildi",
        message: "Garsonumuz kısa süre içinde masanıza gelecektir.",
      });
    } catch (error) {
      if (error.code === "REQUEST_ALREADY_EXISTS") {
        setAssistancePhase("requested");
        pushFeedback({
          variant: "info",
          title: "Garson zaten haberdar edildi",
          message: getApiErrorMessage(error, "Garson isteğiniz zaten iletildi."),
        });
        return;
      }

      setAssistancePhase("idle");
      if (isQrSessionError(error)) {
        setHasValidSession(false);
        setSessionErrorCode(error.code);
        setSessionError(getQrSessionMessage(error.code));
        return;
      }

      pushFeedback({
        variant: "danger",
        title: "Garson çağrılamadı",
        message: getApiErrorMessage(error, "Garson çağrısı gönderilirken hata oluştu."),
      });
    }
  };

  if (loading) {
    return (
      <div className="guest-state-layout">
        <GuestStatusCard
          variant="info"
          title="Menü yükleniyor"
          message="Masa bilgileriniz kontrol ediliyor. Lütfen bekleyin."
          busy
        />
      </div>
    );
  }

  if (!hasValidSession) {
    return (
      <div className="guest-state-layout">
        <GuestStatusCard
          variant="warning"
          title="Oturum Bulunamadı"
          message={sessionError || "QR oturumu doğrulanamadı."}
          actionLabel="QR Kodunu Yeniden Tara"
          onAction={() => navigate(`/qr-scan?reason=${getRecoveryReason({ code: sessionErrorCode })}`)}
        >
          <p className="guest-recovery-note mb-0">
            Yardıma ihtiyacınız olursa lütfen garsonumuzdan destek isteyin.
          </p>
        </GuestStatusCard>
      </div>
    );
  }

  const checkoutLabel =
    checkoutPhase === "redirecting"
      ? "Girişe Yönlendiriliyor..."
      : checkoutPhase === "submitting"
        ? "Sipariş Gönderiliyor..."
        : undefined;
  const assistanceLabel =
    assistancePhase === "requesting"
      ? "Garson Çağrılıyor..."
      : assistancePhase === "requested"
        ? "Garson Haberdar Edildi"
        : "Garsonu Çağır";

  return (
    <div className="container px-3 px-lg-4 guest-page">
      <GuestToastStack items={feedbackItems} onDismiss={dismissFeedback} />

      <div className="guest-order-heading">
        <h1 className="guest-order-heading-title">Menü</h1>
      </div>

      <div className="guest-assistance-panel">
        <div className="guest-assistance-copy">
          <p className="guest-assistance-eyebrow">Masa desteği</p>
          <h2 className="guest-assistance-title">Yardıma mı ihtiyacınız var?</h2>
          <p className="guest-assistance-description">
            Ekibimize haber vermek için aşağıdaki butonu kullanabilirsiniz.
          </p>
        </div>
        <button
          type="button"
          className={`guest-assistance-button ${assistancePhase === "requested" ? "is-requested" : ""}`}
          onClick={handleRequestAssistance}
          disabled={assistancePhase !== "idle"}
        >
          {assistanceLabel}
        </button>
      </div>

      {checkoutPhase === "redirecting" && (
        <GuestStatusCard
          variant="info"
          title="Girişe yönlendiriliyorsunuz"
          message="Siparişi tamamlamak için doğrulama gerekiyor."
          busy
        />
      )}

      {checkoutPhase === "submitting" && (
        <GuestStatusCard
          variant="info"
          title="Siparişiniz gönderiliyor"
          message="Lütfen sayfadan ayrılmayın."
          busy
        />
      )}

      {cartContextNoticeVisible && (
        <GuestStatusCard
          variant="warning"
          title="Yeni masa algılandı"
          message="Başka bir masaya ait sepet bulunduğu için sepetiniz sıfırlandı. Bu masa için yeniden ürün seçebilirsiniz."
          dismissible
          onDismiss={() => setCartContextNoticeVisible(false)}
        />
      )}

      {categories.length > 0 && (
        <div ref={categoryBarRef} className="sticky-top guest-category-bar">
          <div className="guest-category-track">
            <div className="d-flex">
              {categories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  className={`guest-category-chip flex-shrink-0 ${activeCategoryId === category.id ? "is-active" : ""}`}
                  ref={(node) => {
                    if (node) {
                      categoryChipRefs.current.set(category.id, node);
                      return;
                    }

                    categoryChipRefs.current.delete(category.id);
                  }}
                  onClick={() => {
                    setActiveCategoryId(category.id);
                    scrollToCategory(category.id);
                  }}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="guest-page-body">
        <MenuCategoryList menuItems={menu} />
      </div>

      <MobileCart
        onCheckout={handleSubmitOrder}
        checkoutDisabled={checkoutPhase !== "idle"}
        checkoutLabel={checkoutLabel}
      />
    </div>
  );
};

export default OrderPage;
